import React from 'react';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { formatCompactPnl, formatDate, formatDateTime, formatHistoryRollLabel } from './optionsHistoryUtils';
import { useOptionsLocale } from '../../context/OptionsLanguageContext';
import { OptionsMonetaryText } from './OptionsMonetaryText';
import { OptionsUnderlyingStrategyTitle } from './OptionsUnderlyingStrategyTitle';
import { formatLocalizedNumber } from './optionsLocaleFormatting';

export const OptionsHistoryRollCard = ({ event, index, selectedCurrency }) => {
  const { t, isPortuguese } = useOptionsLocale();
  const sourcePnl = formatCompactPnl({
    selectedCurrency,
    nativeValue: event.sourceOperation?.net_pnl_base,
    usdValue: event.sourceOperation?.net_pnl_usd,
    currency: event.sourceOperation?.underlying,
  });
  const targetPnl = formatCompactPnl({
    selectedCurrency,
    nativeValue: event.targetOperation?.net_pnl_base,
    usdValue: event.targetOperation?.net_pnl_usd,
    currency: event.targetOperation?.underlying,
  });
  const campaignPnl = formatCompactPnl({
    selectedCurrency,
    nativeValue: event.campaign?.campaign_net_pnl_base,
    usdValue: event.campaign?.campaign_net_pnl_usd,
    currency: event.campaign?.underlying,
  });

  const sourceStrategy = event.sourceOperation?.strategy_label || event.campaign?.strategy_origin;
  const targetStrategy = event.targetOperation?.strategy_label || event.campaign?.strategy_origin;

  // Reconstrói o contexto da estratégia composta se leg_count > 1
  const isComposite = (event.sourceOperation?.leg_count > 1) || (event.targetOperation?.leg_count > 1);

  const formatLegsList = (legsSummary = []) => {
    if (!legsSummary || !legsSummary.length) return '—';
    return legsSummary.map((leg) => {
      const side = leg.position_side === 'SHORT' ? '' : 'Long ';
      const type = leg.option_type === 'CALL' ? 'CALL' : 'PUT';
      const strike = formatLocalizedNumber(Number(leg.strike || 0), { minimumFractionDigits: 0, maximumFractionDigits: 0 });
      return `${side}${strike} ${type}`;
    }).join(' / ');
  };

  const beforeLegs = isComposite ? formatLegsList(event.sourceOperation?.legs_summary) : null;
  const afterLegs = isComposite ? formatLegsList(event.targetOperation?.legs_summary) : null;

  return (
    <Card className="border-white/10 bg-white/[0.03] text-white shadow-none" data-testid={`options-history-roll-card-${index}`}>
      <CardContent className="space-y-5 p-5 sm:p-6">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-2" data-testid={`options-history-roll-badges-${index}`}>
              <Badge className="border-[#F0B90B]/20 bg-[#F0B90B]/10 text-[#F7D47A]" data-testid={`options-history-roll-type-${index}`}>
                {formatHistoryRollLabel(t)}
              </Badge>
            </div>
            <div>
              <OptionsUnderlyingStrategyTitle
                as="h2"
                underlying={event.targetOperation?.underlying || event.campaign?.underlying}
                strategy={targetStrategy}
                iconSize={18}
                className="text-2xl font-black font-heading tracking-tight text-white sm:text-3xl"
                testId={`options-history-roll-title-${index}`}
              />
              <p className="mt-2 text-sm text-white/45" data-testid={`options-history-roll-subtitle-${index}`}>
                {isPortuguese ? 'Rolado em' : 'Rolled at'} <span className="font-semibold text-white/70">{formatDateTime(event.createdAt, isPortuguese ? 'pt' : 'en')}</span>
              </p>
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-3" data-testid={`options-history-roll-summary-${index}`}>
            <Metric label={t('campaignExplainability.realizedOnRoll')} value={sourcePnl.primary} secondary={sourcePnl.secondary} />
            <Metric label={t('campaignExplainability.currentOperationPnl')} value={targetPnl.primary} secondary={targetPnl.secondary} />
            <Metric label={t('history.campaign')} value={campaignPnl.primary} secondary={campaignPnl.secondary} />
          </div>
        </div>

        {/* Informações detalhadas antes e depois da rolagem para estratégias compostas */}
        {isComposite && (beforeLegs || afterLegs) ? (
          <div className="grid gap-3 border-y border-white/5 py-4 sm:grid-cols-2 text-sm">
            <div className="rounded-2xl border border-white/5 bg-white/[0.01] p-3 space-y-1">
              <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-white/40">{isPortuguese ? 'ANTES' : 'BEFORE'}</p>
              <p className="font-semibold text-white/80 tabular-nums">{beforeLegs}</p>
            </div>
            <div className="rounded-2xl border border-white/5 bg-white/[0.01] p-3 space-y-1">
              <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-white/40">{isPortuguese ? 'DEPOIS' : 'AFTER'}</p>
              <p className="font-semibold text-white/80 tabular-nums">{afterLegs}</p>
            </div>
          </div>
        ) : null}

        <div className="grid gap-3 lg:grid-cols-2" data-testid={`options-history-roll-transitions-${index}`}>
          {event.transitions.map((transition, transitionIndex) => (
            <div key={`${transition.optionType}-${transitionIndex}`} className="rounded-2xl border border-white/10 bg-black/20 px-4 py-4" data-testid={`options-history-roll-transition-${index}-${transitionIndex}`}>
              <p className="text-[11px] uppercase tracking-[0.18em] text-white/40">
                {isPortuguese ? 'Perna Rolada' : 'Rolled Leg'} ({transition.positionSide} {transition.optionType})
              </p>
              <p className="mt-2 text-base font-semibold text-white tabular-nums">
                {formatLocalizedNumber(Number(transition.sourceStrike || 0), { minimumFractionDigits: 0, maximumFractionDigits: 0 })} <span className="mx-1.5 text-white/35">→</span> {formatLocalizedNumber(Number(transition.targetStrike || 0), { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
              </p>
              {transition.sourceExpiration !== transition.targetExpiration ? (
                <p className="mt-1.5 text-xs text-white/50" data-testid={`options-history-roll-expiry-${index}-${transitionIndex}`}>
                  {isPortuguese ? 'Vencimento' : 'Expiration'}: {formatDate(transition.sourceExpiration, isPortuguese ? 'pt' : 'en')} → {formatDate(transition.targetExpiration, isPortuguese ? 'pt' : 'en')}
                </p>
              ) : (
                <p className="mt-1.5 text-xs text-white/50" data-testid={`options-history-roll-expiry-${index}-${transitionIndex}`}>
                  {isPortuguese ? 'Mesmo vencimento' : 'Same expiration'}: {formatDate(transition.sourceExpiration, isPortuguese ? 'pt' : 'en')}
                </p>
              )}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

const Metric = ({ label, value, secondary = null }) => (
  <div className="rounded-2xl border border-white/10 bg-black/20 px-4 py-4">
    <p className="text-[11px] uppercase tracking-[0.18em] text-white/40">{label}</p>
    <p className="mt-2 text-sm font-semibold text-white tabular-nums"><OptionsMonetaryText value={value} iconSize={15} /></p>
    {secondary ? <p className="mt-1 text-xs text-white/50 tabular-nums"><OptionsMonetaryText value={secondary} iconSize={13} /></p> : null}
  </div>
);

export default OptionsHistoryRollCard;